import { useCallback, useEffect, useState } from 'react';
import {
  applyPlaidItemAccountMapping,
  exchangePlaidPublicToken,
  getPlaidLinkToken,
  updatePlaidItemAfterRefresh,
  useGraphqlClient,
} from '../graphql';
import {
  ApplyPlaidItemAccountMappingInput,
  AppType,
  PlaidItemAccountMapping,
  PlaidItemAccountMappingInput,
} from '../generated/graphql';
import { PlaidLinkError, PlaidLinkOnExit, PlaidLinkOnSuccess, usePlaidLink } from 'react-plaid-link';
import { useQueryClient } from '@tanstack/react-query';
import { investmentKeys } from '../Investment';
import { cashAccountKeys } from '../CashAccount';
import { ttDetailsKeys } from '../TtDetails';
import { loanKeys } from '../Loan/queryKeys';
import { useRevalidator } from '@remix-run/react';

export const useGenerateLinkToken = (householdID: string, plaidItemID?: string | null) => {
  const client = useGraphqlClient();
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { linkToken } = await getPlaidLinkToken(client, { appType: AppType.Web, householdID, plaidItemID });
      setToken(linkToken);
    })();
  }, []);

  return token;
};

export const usePlaidConnection = ({ householdID, linkToken, afterExit }: { householdID: string; linkToken: string | null; afterExit?: () => void; }) => {
  const client = useGraphqlClient();
  const [plaidItems, setPlaidItems] = useState<PlaidItemAccountMapping[]>([]);

  const onSuccess = useCallback<PlaidLinkOnSuccess>((publicToken) => {
    // send Plaid public token to the backend to exchange
    // TODO: check for duplicate before exchanging the public token: dev-2282
    (async () => {
      const plaidItemsPayload = await exchangePlaidPublicToken(client, {
        householdID,
        publicToken,
        appType: AppType.Web,
      });
      setPlaidItems((prevState) => [...prevState, ...plaidItemsPayload.accountsMapping]);
    })();
  }, []);

  const onExit = useCallback<PlaidLinkOnExit>((error: PlaidLinkError | null) => {
      // handle invalid Link token, e.g. if the Link token has expired
      if (error != null && error.error_code === 'INVALID_LINK_TOKEN') {
        getPlaidLinkToken(client, { appType: AppType.Web, householdID });
      }
      afterExit?.();
    },
    [],
  );
  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess, onExit });

  return { open, ready, plaidItems, setPlaidItems };
};

export const useSavePlaidItems = () => {
  const client = useGraphqlClient();
  const queryClient = useQueryClient();

  const saveAccountMappings = async ({ householdID, accountMappings }: { householdID: string; accountMappings: PlaidItemAccountMapping[] }) => {
    const newAccounts: PlaidItemAccountMappingInput[] = accountMappings.map((mapping) => {
      return {
        plaidItemID: mapping.plaidItemID,
        plaidItemAccountID: mapping.plaidItemAccountID,
        name: mapping.name,
        officialName: mapping.officialName,
        mappedEntityID: mapping.mappedEntityID,
        mappedType: mapping.mappedType,
        mappedCashAccountType: mapping.mappedCashAccountType,
        mappedLoanType: mapping.mappedLoanType,
        mappedInvestmentType: mapping.mappedInvestmentType,
        balance: mapping.balance,
      };
    });
    const input: ApplyPlaidItemAccountMappingInput = {
      householdID,
      accountsMapping: newAccounts,
      appType: AppType.Web,
    };

    return applyPlaidItemAccountMapping(client, input).then((result) =>
      Promise.all([
        queryClient.invalidateQueries(investmentKeys.investments(householdID)),
        queryClient.invalidateQueries(cashAccountKeys.cashAccounts(householdID)),
        queryClient.invalidateQueries(loanKeys.loans(householdID)),
        queryClient.invalidateQueries(ttDetailsKeys.base),
      ]).then(() => result)
    );
  };

  return { saveAccountMappings };
};

export const useUpdatePlaidConnection = ({ householdID, plaidItemID, linkToken }: { householdID: string, plaidItemID: string, linkToken: string | null }) => {
  const client = useGraphqlClient();
  const { revalidate } = useRevalidator();

  const onSuccess = useCallback<PlaidLinkOnSuccess>(() => {
    (async () => {
      updatePlaidItemAfterRefresh(client, { householdID, plaidItemID, appType: AppType.Web })
        .then(() => revalidate());
    })();
  }, []);

  const { open } = usePlaidLink({ token: linkToken, onSuccess });

  return { open };
};
